import { Autocomplete, Box } from "@mui/material";
import { Form, Formik } from "formik";
import { forwardRef, useImperativeHandle, useRef } from "react";
import * as yup from "yup";
import FormTextField from "../../components/forms/FormTextField";
import TimezoneSelect from "../../components/forms/TimezoneSelect";
import TenantRatingField from "./TenantRatingField";
import TenantSLAField from "./TenantSLAField";

const AddNewTenantForm = forwardRef(
  (
    {
      onSubmit,
      initialValues = {},
      isDisabledCompanyDomain = false,
      isDisabledTimezone = false,
    },
    ref
  ) => {
    const formRef = useRef(null);

    // Use the initial values if provided, otherwise use the default values
    const formInitialValues = {
      companyName: initialValues?.companyName || "",
      companyDomain: initialValues?.companyDomain || "",
      timezone: initialValues?.timezone || "",
      importance: initialValues?.importance || 0,
      labels: initialValues?.labels || [],
      sla: {
        Critical: initialValues?.sla?.Critical || 30,
        High: initialValues?.sla?.High || 60,
        Medium: initialValues?.sla?.Medium || 90,
        Low: initialValues?.sla?.Low || 120,
      },
    };

    const formValidationSchema = yup.object().shape({
      companyName: yup.string().required("Company name is required"),
      companyDomain: yup
        .string()
        .required("Company domain is required")
        .matches(
          /^[a-zA-Z0-9.-]+$/,
          "Company domain can only contain letters, numbers, dots, and hyphens"
        ),
      timezone: isDisabledTimezone
        ? yup.string()
        : yup.string().required("Timezone is required"),
      importance: yup
        .number()
        .min(1, "Importance must be at least 1")
        .max(5, "Importance must be at most 5")
        .required("Importance is required"),
      sla: yup
        .object()
        .shape({
          Critical: yup
            .number()
            .typeError("Critical SLA must be a number")
            .required("Critical SLA is required"),
          High: yup
            .number()
            .typeError("High SLA must be a number")
            .required("High SLA is required"),
          Medium: yup
            .number()
            .typeError("Medium SLA must be a number")
            .required("Medium SLA is required"),
          Low: yup
            .number()
            .typeError("Low SLA must be a number")
            .required("Low SLA is required"),
        })
        .required("SLA is required"),
    });

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formRef.current) {
          formRef.current.handleSubmit();
        }
      },
    }));

    return (
      <Box sx={{ width: 420, overflowY: "auto" }}>
        <Formik
          innerRef={formRef}
          onSubmit={onSubmit}
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          enableReinitialize
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  paddingRight: 2,
                }}
              >
                <FormTextField
                  label="Company name"
                  fieldName="companyName"
                  required
                />
                <FormTextField
                  label="Company domain"
                  fieldName="companyDomain"
                  required
                  disabled={isDisabledCompanyDomain}
                  helperText="company.com"
                />
                <TimezoneSelect required disabled={isDisabledTimezone} />

                <TenantRatingField />
                <TenantSLAField />
                <Autocomplete
                  options={[]}
                  value={values.labels}
                  multiple
                  freeSolo
                  filterSelectedOptions
                  onChange={(_, newValue) => setFieldValue("labels", newValue)}
                  renderInput={(params) => (
                    <FormTextField
                      {...params}
                      label="Add custom label (e.g., Project, High Priority)"
                      fieldName="labels"
                      emptyOnChange
                    />
                  )}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    );
  }
);

AddNewTenantForm.displayName = "AddNewTenantForm";

export default AddNewTenantForm;
